import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';

import ReadSurahSelector from '../../selector';
import AppTitle from '@/components/common/title';
import { InfoOutlined } from '@mui/icons-material';
import { routes } from '@/infrastructure/const/routes';
import PlayControlButton from '@/components/buttons/play';
import ReciterSelector from '@/components/reciters/selector';
import { replaceWords } from '@/utils/helpers/replace-words';
import { ReadSurah } from '@/infrastructure/class/read-surah';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import { useAuth } from '@/infrastructure/context/auth-context';
import BookmarkControlButton from '@/components/buttons/bookmark';
import AudioSurahSelector from '@/components/audio-surahs/selector';
import { usePlayer } from '@/infrastructure/context/player-context';
import TranslationsSelector from '@/components/translations/selector';
import { ITranslation } from '@/infrastructure/interfaces/i-translation';
import { ReciterAudioSurah } from '@/infrastructure/class/reciter-audio-surah';
import { useReadBookmarks } from '@/infrastructure/context/read-bookmarks-context';
import { useAudioBookmarks } from '@/infrastructure/context/audio-bookmarks-context';
import s from './styles.module.scss';

interface IReadSurahDetailHeaderProps {
  readSurah: ReadSurah;
  audioSurah?: AudioSurah;
  readSurahs?: ReadSurah[];
  audioSurahs?: AudioSurah[];
  translations: ITranslation[];
  ayasCount: number;
  translation: string;
  reciters?: ReciterAudioSurah[];
  reciterSlug?: string;
  withDetail?: boolean;
  className?: string;
}

const ReadSurahDetailHeader: React.FC<IReadSurahDetailHeaderProps> = (
  props
) => {
  const {
    readSurah,
    audioSurah,
    readSurahs = [],
    audioSurahs = [],
    translations,
    translation,
    reciters,
    reciterSlug,
    ayasCount,
    withDetail = false,
    className = '',
  } = props;

  const { player, setPlaying, setTracks } = usePlayer();
  const { audioBookmarks, changeAudioBookmark } = useAudioBookmarks();
  const { readBookmarks, changeReadBookmark, isBookmarked } =
    useReadBookmarks();

  const [isShort, setIsShort] = useState<boolean>(false);
  const [animate, setAnimate] = useState<boolean>(false);

  const { authUser, checkAuth, showMessage } = useAuth();

  const { t: tButtons } = useTranslation('buttons');

  const bookmark = audioSurah
    ? audioBookmarks.isBookmark(audioSurah, 1)
    : isBookmarked(readSurah, 1);

  const historyRoute = replaceWords(routes.surahReadHistory, {
    suraSlug: readSurah.slug,
  });

  const playState =
    player.playing && player.currentTrack?.number === readSurah.index;

  const headerClassNames = classNames(s['header'], 'animate__animated', {
    [s['short']]: isShort,
    animate__fadeIn: animate,
  });
  const headerWrapperClassNames = classNames(s['header-wrapper'], className);
  const controlsClassNames = classNames(s['controls'], {
    [s['controls-short']]: isShort,
  });
  const mobLangSelectorClassNames = classNames(s['lang-selector'], {
    [s['short']]: isShort,
  });

  const onPlay = () => {
    if (reciters) {
      const tracks = reciters.map((item) => item.audioSurah);
      const audioSurah = tracks[0];
      if (
        !player.currentTrack ||
        player.currentTrack.number !== audioSurah.number
      ) {
        setTracks(tracks, audioSurah);
        return;
      }
    }
    if (player.currentTrack) {
      if (player.playing) {
        setPlaying(false);
      } else {
        setPlaying(true);
      }
    }
  };

  const onBookmark = async () => {
    await checkAuth();
    if (authUser) {
      if (audioSurah)
        changeAudioBookmark({
          user: authUser,
          audioSurah,
          timing: 1,
        });
      if (readSurah)
        changeReadBookmark({
          user: authUser,
          readSurah,
          ayaNumber: 1,
        });

      const message = bookmark
        ? tButtons('removedFromBookmarks')
        : tButtons('addedToBookmarks');
      showMessage(message);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsShort(true);
      }

      if (window.scrollY === 0) {
        setIsShort(false);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setAnimate(true);
    setTimeout(() => {
      setAnimate(false);
    }, 600);
  }, [isShort]);

  return (
    <div className={headerWrapperClassNames}>
      <div className={headerClassNames}>
        <div className={s['info']}>
          <div>
            <h2 className={s['title']}>
              {readSurah.titleEn}
              {withDetail && (
                <Link href={historyRoute} title={tButtons('readingHistory')}>
                  <IconButton
                    className={s['info-btn']}
                    aria-label={tButtons('readingHistory')}
                  >
                    <InfoOutlined />
                  </IconButton>
                </Link>
              )}
            </h2>
            <AppTitle
              className={s['transliteration']}
              title={readSurah.transliteration}
              main
            />
          </div>
          <div className={s['btns']}>
            <PlayControlButton active={playState} onClick={onPlay} />
            <BookmarkControlButton active={!!bookmark} onClick={onBookmark} />
          </div>
          <div className={mobLangSelectorClassNames}>
            <TranslationsSelector
              items={translations}
              activeTranslation={translation}
            />
          </div>
        </div>

        <div className={controlsClassNames}>
          <div className={s['mob-btns']}>
            <PlayControlButton active={playState} onClick={onPlay} />
            <BookmarkControlButton active={!!bookmark} onClick={onBookmark} />
          </div>
          <div className={s['additional-selectors']}>
            {!!readSurahs.length && (
              <ReadSurahSelector
                surahs={readSurahs}
                currentSurah={readSurah}
                ayasCount={ayasCount}
                className={s['surah-selector']}
              />
            )}
            {!!audioSurahs.length && audioSurah && (
              <AudioSurahSelector
                surahs={audioSurahs}
                currentSurah={audioSurah}
                reciterSlug={reciterSlug}
                className={s['surah-selector']}
              />
            )}
            {reciters && (
              <ReciterSelector
                items={reciters}
                className={s['reciter-selector']}
              />
            )}
          </div>
          <TranslationsSelector
            items={translations}
            activeTranslation={translation}
          />
        </div>
      </div>
    </div>
  );
};

export default ReadSurahDetailHeader;
